import {useState} from 'react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {useAutomatedReviewCommentActions} from '../hooks/use-automated-review-comment-actions'
import {BaseSharedDismissalDialog} from '@github-ui/shared-dismissal/base-shared-dismissal-dialog'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {AutomatedCommentSource} from '@github-ui/diff-lines/conversation-types'

export interface AutomatedComment {
  id: string
  isDismissed: boolean
  viewerCanDismiss: boolean
  viewerCanReopen: boolean
  source: string
  dismissalOptions: Array<{value: string; label: string; description: string}>
}
interface BaseAutomatedReviewCommentActionsProps {
  automatedComment: AutomatedComment
  isButtonDisabled: boolean
  dismissError: Error | null
  onDismiss: (dismissalPayload: {reason: string; resolutionNote?: string}, callback: {onSuccess: () => void}) => void
  onReopen: () => void
  className?: string
}

export function BaseAutomatedReviewCommentActions({
  automatedComment,
  isButtonDisabled,
  dismissError,
  onDismiss,
  onReopen,
  className,
}: BaseAutomatedReviewCommentActionsProps) {
  const [dismissDialogIsOpen, setDismissDialogIsOpen] = useState(false)
  const {isDismissable, isReopenable, resourceName, toggleButtonLabel} =
    useAutomatedReviewCommentActions(automatedComment)
  const isDismissButtonDisabled = isButtonDisabled || !automatedComment.viewerCanDismiss
  const isReopenButtonDisabled = isButtonDisabled || !automatedComment.viewerCanReopen

  return (
    <>
      {dismissDialogIsOpen && (
        <BaseSharedDismissalDialog
          setOpen={setDismissDialogIsOpen}
          count={1}
          error={dismissError}
          dismissalOptions={automatedComment.dismissalOptions}
          onSubmit={({resolution, dismissalComment}) =>
            onDismiss(
              {reason: resolution, resolutionNote: dismissalComment},
              {onSuccess: () => setDismissDialogIsOpen(false)},
            )
          }
          delegatedAlertDismissalEnabled={false}
          codeSecurity={automatedComment.source === AutomatedCommentSource.CodeScanning}
        />
      )}
      {isDismissable && (
        <div className={clsx('d-flex flex-items-center flex-wrap', className)}>
          <Button
            aria-label={toggleButtonLabel}
            className="mr-2"
            onClick={() => setDismissDialogIsOpen(true)}
            disabled={isDismissButtonDisabled}
          >
            {toggleButtonLabel}
          </Button>
          <span className="fgColor-muted f6">
            Dismissing the {resourceName} will mark this conversation as resolved.
          </span>
        </div>
      )}
      {isReopenable && (
        <div className={clsx('d-flex flex-items-center flex-wrap', className)}>
          <Button aria-label={toggleButtonLabel} onClick={onReopen} disabled={isReopenButtonDisabled}>
            {toggleButtonLabel}
          </Button>
        </div>
      )}
    </>
  )
}

try{ BaseAutomatedReviewCommentActions.displayName ||= 'BaseAutomatedReviewCommentActions' } catch {}